import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Building2, Plus, Landmark } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (v) => (v || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const statusColor = (s) => ({
  Active: 'bg-green-100 text-green-700',
  Created: 'bg-blue-100 text-blue-700',
  Retired: 'bg-gray-200 text-gray-600',
}[s] || 'bg-gray-100');

const EMPTY = {
  description: '', asset_class: '', company_code: '', cost_center: '',
  useful_life_years: 5, depreciation_method: 'STRAIGHT_LINE', salvage_value: 0, currency: 'USD',
};

const AssetList = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const writable = canWrite('asset_master');
  const [assets, setAssets] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/assets`, { withCredentials: true });
      setAssets(res.data);
    } catch (e) {
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadClasses = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/assets/asset-classes`, { withCredentials: true });
      setClasses(res.data);
    } catch (e) { /* non-blocking */ }
  }, []);

  useEffect(() => { load(); loadClasses(); }, [load, loadClasses]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onClassChange = (v) => {
    const cls = classes.find((c) => c.asset_class === v);
    setForm((p) => ({
      ...p, asset_class: v,
      useful_life_years: cls ? cls.default_useful_life_years : p.useful_life_years,
      depreciation_method: cls ? cls.default_depreciation_method : p.depreciation_method,
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/assets`, form, { withCredentials: true });
      toast.success(`Asset ${res.data.asset_number} created`);
      setOpen(false);
      setForm(EMPTY);
      navigate(`/assets/${res.data.asset_number}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to create asset');
    } finally {
      setSaving(false);
    }
  };

  const filtered = statusFilter === 'all' ? assets : assets.filter((a) => a.status === statusFilter);
  const kpi = assets.reduce((acc, a) => {
    acc.apc += a.acquisition_value || 0;
    acc.accum += a.accumulated_depreciation || 0;
    acc.nbv += (a.status === 'Retired' ? 0 : (a.net_book_value || 0));
    if (a.status === 'Active') acc.active += 1;
    return acc;
  }, { apc: 0, accum: 0, nbv: 0, active: 0 });

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="asset-register-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100 text-amber-700"><Landmark className="h-5 w-5" /></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Asset Register</h1>
              <p className="text-gray-600 text-sm mt-0.5">Fixed assets, values and lifecycle status.</p>
            </div>
          </div>
          {writable && (
            <Button onClick={() => setOpen(true)} data-testid="new-asset-btn"><Plus className="h-4 w-4 mr-2" />New Asset</Button>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            ['Total Assets', assets.length, 'text-gray-900'],
            ['Acquisition Value', money(kpi.apc), 'text-blue-700'],
            ['Accumulated Dep.', money(kpi.accum), 'text-orange-700'],
            ['Net Book Value', money(kpi.nbv), 'text-green-700'],
          ].map(([label, val, color]) => (
            <Card key={label} className="p-4">
              <div className="text-xs text-gray-500">{label}</div>
              <div className={`text-2xl font-bold mt-1 ${color}`} data-testid={`kpi-${label}`}>{val}</div>
            </Card>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <Label className="text-sm text-gray-500">Status</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48" data-testid="asset-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="Created">Created</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Retired">Retired</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Asset #</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Class</TableHead>
                <TableHead>Company</TableHead>
                <TableHead className="text-right">APC</TableHead>
                <TableHead className="text-right">Accum Dep</TableHead>
                <TableHead className="text-right">NBV</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">Loading...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">No assets found.</TableCell></TableRow>
              ) : filtered.map((a) => (
                <TableRow key={a.asset_number} className="cursor-pointer hover:bg-gray-50"
                  onClick={() => navigate(`/assets/${a.asset_number}`)} data-testid={`asset-row-${a.asset_number}`}>
                  <TableCell className="font-medium">{a.asset_number}</TableCell>
                  <TableCell>{a.description}</TableCell>
                  <TableCell>{a.asset_class}</TableCell>
                  <TableCell>{a.company_code}</TableCell>
                  <TableCell className="text-right">{money(a.acquisition_value)}</TableCell>
                  <TableCell className="text-right">{money(a.accumulated_depreciation)}</TableCell>
                  <TableCell className="text-right font-medium">{money(a.status === 'Retired' ? 0 : a.net_book_value)}</TableCell>
                  <TableCell><span className={`px-2 py-1 rounded text-xs font-medium ${statusColor(a.status)}`}>{a.status}</span></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Asset</DialogTitle>
            <DialogDescription>Create the asset master record. Post an acquisition afterwards to capitalize it.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <Label>Description</Label>
              <Input data-testid="asset-description" value={form.description} onChange={(e) => set('description', e.target.value)} />
            </div>
            <div>
              <Label>Asset Class</Label>
              <Select value={form.asset_class} onValueChange={onClassChange}>
                <SelectTrigger data-testid="asset-class-select"><SelectValue placeholder="Select class" /></SelectTrigger>
                <SelectContent>
                  {classes.map((c) => <SelectItem key={c.asset_class} value={c.asset_class}>{c.asset_class} — {c.description}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Company Code</Label>
              <Input data-testid="asset-company-code" value={form.company_code} onChange={(e) => set('company_code', e.target.value)} placeholder="1000" />
            </div>
            <div>
              <Label>Cost Center</Label>
              <Input data-testid="asset-cost-center" value={form.cost_center} onChange={(e) => set('cost_center', e.target.value)} />
            </div>
            <div>
              <Label>Useful Life (years)</Label>
              <Input type="number" min={1} max={100} data-testid="asset-life" value={form.useful_life_years}
                onChange={(e) => set('useful_life_years', parseInt(e.target.value, 10) || 1)} />
            </div>
            <div>
              <Label>Depreciation Method</Label>
              <Select value={form.depreciation_method} onValueChange={(v) => set('depreciation_method', v)}>
                <SelectTrigger data-testid="asset-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="STRAIGHT_LINE">Straight Line</SelectItem>
                  <SelectItem value="DECLINING_BALANCE">Declining Balance</SelectItem>
                  <SelectItem value="NONE">No Depreciation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Salvage Value</Label>
              <Input type="number" min={0} data-testid="asset-salvage" value={form.salvage_value}
                onChange={(e) => set('salvage_value', parseFloat(e.target.value) || 0)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.description || !form.asset_class || !form.company_code} data-testid="save-asset-btn">
              {saving ? 'Creating...' : 'Create Asset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AssetList;
